import { Company } from '@app/features/companies/company.model';
import { AssignmentType } from '@app/features/contracts/embauche-dialog/embauche-tabs-form/assignment-form/assignment-type.model';
import { SignatureRequest } from '@app/features/contracts/signature/signature-request.model';
import { ValidationRequest } from '@app/features/contracts/validation/validation-request.model';
import { DocumentsMetadata } from '@app/features/documents/document-metadata.model';
import { CdTDocument, DocumentTag, DocumentTagAndId } from '@app/features/documents/document.model';
import { Employee, PersonTitle } from '@app/features/employees/employee.model';
import { Schedule } from '@app/features/horaires/schedules.model';
import { JobType } from '@app/features/job-types/job-type.model';
import { Residency } from '@app/features/residences/residency.model';
import {
  SalaryScaleContractUpdate,
  SalaryScaleContractUpdateListItem,
} from '@app/features/salary/salary-scale.model';
import { PageableSortableSearch } from '@app/shared/searchable.model';

export interface Contract {
  id?: string;
  adpEditedAt: string | null;
  adpEditedBy: string | null;
  signatureSendFailedAt: string | null;
  signatureSendFailedBy: string | null;
  signatureSendFailureReason: string | null;
  company: Company;
  employee: Employee | Employee;
  employeeId: string | null;
  endOfFirstTrialPeriodDate: string | null;
  contract: ContractPart;
  transferToAnotherCompany?: boolean;
  /**
   * À l'ouverture d'un nouveau contrat : une prolongation ou un renouvellement sans interruption,
   * qui part d'un contrat précédent. Saisi par l'écran, jamais transmis à l'API.
   */
  renewal?: boolean;
  schedule: SchedulePart;
  assignmentType: AssignmentType;
  residencies: Residency[];
  documents: CdTDocument[];
  documentsMetadata: DocumentsMetadata;
  status: ContractStatus;
  validationRequests: ValidationRequest[];
  salaryScaleContractUpdates: SalaryScaleContractUpdate[];
  signatureRequests?: SignatureRequest[];
  createdAt: string;
  createdBy: string;
  deleted: boolean;
  deletedAt: string | null;
  deletedBy: string | null;
  deletionComment: string | null;
}

export interface SchedulePart {
  collectiveSchedules: boolean | null;
  schedules?: Schedule[];
}

export interface ContractPart {
  firstContractId: string | null;
  contractType: ContractType | null;
  startDate: string | null; // ISO date string
  endDate: string | null; // ISO date string
  endOfFirstTrialPeriodDate: string | null; // ISO date string
  replacementCivility: PersonTitle | null;
  replacementReason: string | null;
  replacementFirstName: string | null;
  replacementLastName: string | null;
  replacementJobType: JobType | null;
  replacementQualificationLevel: string | null;
  jobType: JobType | null;
  qualificationLevel: string | null;
  grossHourlySalary: number;
  companyCar: boolean;
  previousContractId: string | null;
}

export const ContractTypes = ['CDD R', 'CDD RST', 'CDD A', 'CDI'] as const;
export type ContractType = (typeof ContractTypes)[number];

export interface EditContractPart {
  contractType: ContractType | null;
  startDate: string | null; // ISO date string
  endDate: string | null; // ISO date string
  endOfFirstTrialPeriodDate: string | null; // ISO date string
  replacementCivility: PersonTitle | null;
  replacementReason: string | null;
  replacementFirstName: string | null;
  replacementLastName: string | null;
  replacementJobId: number | null;
  replacementQualificationLevel: string | null;
  jobId: number | null;
  qualificationLevel: string | null;
  grossHourlySalary: number | null;
  previousContractId: string | null;
  companyCar: boolean;
}

export interface EditContract {
  id?: string;
  company: {
    id?: string;
    name?: string;
  };
  employee: Employee | Employee;
  contract: EditContractPart | null;
  transferToAnotherCompany?: boolean | null;
  schedule: {
    collectiveSchedules: boolean | null;
    schedules: Schedule[] | null;
  };
  assignmentType: AssignmentType | null;
  residencies: Residency[] | null;
  documents: DocumentTagAndId[];
  documentsMetadata: DocumentsMetadata | null;
}

/**
 * L'avenant ni signé ni abandonné d'un contrat (cadrage 2026-014), sous le numéro qu'il porte ou
 * recevra à l'acceptation du DRH.
 */
export interface AmendmentInProgress {
  id: string;
  number: number;
  status: ContractStatus;
  effectiveDate: string;
  /** Seulement pour un avenant temporaire. */
  endDate: string | null;
  /** Ses demandes de signature : la sienne reste à demander tant qu'aucune n'est en cours. */
  signatureRequests: SignatureRequest[];
  /** Ses demandes de validation : les gestes de son circuit que sa ligne propose. */
  validationRequests?: ValidationRequest[] | null;
  /** Son dernier envoi en signature échoué, comme sur le contrat (RG-signature-echec-visible). */
  signatureSendFailedAt?: string | null;
  signatureSendFailedBy?: string | null;
  signatureSendFailureReason?: string | null;
  /** Sa coche « Édité » (RG-avenant-coche-edite). */
  adpEditedAt?: string | null;
  adpEditedBy?: string | null;
}

export interface ContractListItem {
  id: string;
  adpEditedAt: string | null;
  adpEditedBy: string | null;
  /** Dernier envoi en signature échoué, effacé par l'API au premier envoi réussi. */
  signatureSendFailedAt: string | null;
  signatureSendFailedBy: string | null;
  signatureSendFailureReason: string | null;
  firstContractId: string;
  employeeFirstname: string | null;
  employeeLastname: string | null;
  replacementFullName: string | null;
  endOfFirstTrialPeriodDate: string | null;
  companyId: string;
  contractType: ContractType | null;
  startDate: string | null;
  endDate: string | null;
  documentTags: DocumentTag[];
  status: ContractStatus;
  validationRequests?: ValidationRequest[];
  salaryScaleContractUpdates: SalaryScaleContractUpdateListItem[];
  signatureRequests?: SignatureRequest[];
  createdAt: string;
  createdBy: string;
  deleted: boolean;
  deletedAt: string | null;
  deletedBy: string | null;
  deletionComment: string | null;
  amendmentInProgress?: AmendmentInProgress | null;
  /** Les avenants du contrat, quel que soit leur état : ils rejoignent son historique. */
  amendmentCount?: number;
}

export function toContractListItem(contract: Contract): ContractListItem {
  return {
    id: contract.id!,
    adpEditedAt: contract.adpEditedAt ?? null,
    adpEditedBy: contract.adpEditedBy ?? null,
    signatureSendFailedAt: contract.signatureSendFailedAt ?? null,
    signatureSendFailedBy: contract.signatureSendFailedBy ?? null,
    signatureSendFailureReason: contract.signatureSendFailureReason ?? null,
    endOfFirstTrialPeriodDate: contract.endOfFirstTrialPeriodDate ?? null,
    firstContractId: contract.contract?.firstContractId ?? contract.id!,
    employeeFirstname: contract.employee.firstname,
    employeeLastname: contract.employee.lastname,
    replacementFullName:
      `${contract.contract?.replacementFirstName ?? ''} ${contract.contract?.replacementLastName ?? ''}`.trim() ||
      null,
    companyId: contract.company.id,
    contractType: contract.contract?.contractType ?? null,
    startDate: contract.contract?.startDate ?? null,
    endDate: contract.contract?.endDate ?? null,
    documentTags: contract.documents.map((doc) => doc.tag),
    status: contract.status,
    validationRequests: contract.validationRequests,
    salaryScaleContractUpdates:
      contract.salaryScaleContractUpdates?.map((update) => ({
        id: update.id,
        previousGrossHourlySalary: update.previousGrossHourlySalary,
        newGrossHourlySalary: update.newGrossHourlySalary,
        updatedAt: update.updatedAt,
        salaryScaleEntryId: update.salaryScaleEntry.id!,
      })) ?? [],
    signatureRequests: contract.signatureRequests ?? [],
    createdAt: contract.createdAt ?? '',
    createdBy: contract.createdBy ?? '',
    deleted: contract.deleted ?? false,
    deletedAt: contract.deletedAt ?? null,
    deletedBy: contract.deletedBy ?? null,
    deletionComment: contract.deletionComment ?? null,
  };
}

export type ContractSearch = PageableSortableSearch & {
  employeeFirstname?: string | null;
  employeeLastname?: string | null;
  createdByUserId?: string | null;
  pendingValidationRequestRequestedToUserId?: string | null;
  companyId?: string | null;
  statuses?: ContractStatus[] | null;
  /** Vrai : avec un avenant en cours ; faux : sans ; vide : tous. */
  amendmentInProgress?: boolean | null;
  deleted?: boolean | null;
};

export type ContractStatus =
  | 'DRAFT'
  | 'REX_VALIDATING'
  | 'REX_VALIDATED'
  | 'DA_VALIDATING'
  | 'DA_VALIDATED'
  | 'DP_VALIDATING'
  | 'DP_VALIDATED'
  | 'RRH_VALIDATING'
  | 'RRH_VALIDATED'
  | 'DRH_VALIDATING'
  | 'SIGNING'
  | 'SIGNED'
  | 'CANCELED';

export const contractStatusOrdered: ContractStatus[] = [
  'DRAFT',
  'REX_VALIDATING',
  'REX_VALIDATED',
  'DA_VALIDATING',
  'DA_VALIDATED',
  'DP_VALIDATING',
  'DP_VALIDATED',
  'RRH_VALIDATING',
  'RRH_VALIDATED',
  'DRH_VALIDATING',
  'SIGNING',
  'SIGNED',
  'CANCELED',
];
